import { format } from "date-fns";
import { SEA, AIR, SEA_COUNT, AIR_COUNT } from "../consts/boxTypes";
import { getAllowedUserPermissions } from "./userPermissions";
import { 
    EMAILS_TYPE, 
    RECEIVED_INVENTORY_TYPE, 
    WAREHOUSE_INVENTORY_TYPE, 
    RECEIVED_SHIPMENTS_TYPE, 
    INVENTORY_COST_CHANGE_TYPE, 
    SHIPPING_EACH_COST_CHANGE_TYPE, 
    EADIN_COGS_TYPE, 
    LOW_STOCK_ALERTS_TYPE, 
    AMAZON_FBA_LOW_STOCK_ALERTS_TYPE,
    LOW_STOCK_SOLD_OUT_ALERTS_TYPE, 
    FINANCE_TYPE, 
    QUICKBOOKS_TYPE, 
    AMAZON_ORDERS_TYPE, 
    ORDERS_RECONCILE_TYPE, 
    PROFIT_REPORT_ALL, 
    SHIPMENT_COSTS_TYPE, 
    PRODUCTS_TYPE, 
    SHOPIFY_AMAZON_INVENTORY, 
    BEGIN_INVENTORY, 
    OTB_ORDERS, 
    SHOPIFY_PRODUCTS, 
    SHOPIFY_LANDING_COST, 
    SHOPIFY_METAFIELDS, 
    SHOPIFY_METAFIELDS_UPDATE,
    SHOPIFY_AMAZON_SALES, 
    SHOPIFY_REFUNDS, 
    META_INSIGHTS_TYPE,
    META_ADS_TYPE,
    AMAZON_SALES_REPORT,
    CASH_FLOW_REPORT_ALL, 
    MISSING_INVENTORY_COST, 
    BALANCE_SHEET_REPORT_ALL, 
    SHIPHERO_TOKEN_TYPE, 
    BOM_PACKAGING_TYPE, 
    CHECK_PRODUCT_SALE_STATUS_TYPE,
    SOURCE_SKUS_BY_PRODUCT_TYPE, 
    AMAZON_MONTH_END_CLOSE_SUMMARY_TYPE, 
    INBOUND_SHIPMENTS_TYPE, 
    MONTHLY_AP_PO_PAID_TYPE, 
    MONTHLY_PO_AP_DUE_TYPE, 
    MONTHLY_PO_AP_PROGRESS_TYPE, 
    SALES_BY_PRODUCT_TYPE, 
    SALES_BY_PRODUCT_BY_DAY_TYPE, 
    SALES_REVENUE_TYPE, 
    SALES_REVENUE_MONTHLY_TYPE, 
    SHOPIFY_PROFIT_SUMMARY_TYPE, 
    SHOPIFY_PROFIT_BY_SKU_SUMMARY_TYPE, 
    TOTAL_PRODUCT_REVENUE_TYPE, 
    KIT_PRODUCTS_TYPE,
    KLAVIYO_TYPE,
    POSTSCRIPT_TYPE,
    GOOGLE_ADS_METRICS_TYPE,
    MICROSOFT_ADS_METRICS_TYPE,
    APPLOVIN_METRICS_TYPE,
    TIKTOK_ADS_METRICS_TYPE,
    SNAPCHAT_ADS_METRICS_TYPE,
    X_ADS_METRICS_TYPE,
    GA4_METRICS_TYPE,
    STRIPE_SYNC_TYPE,
    ALIA_METRICS_TYPE,
    TRYBE_METRICS_TYPE,
    FATHOM_MEETINGS_TYPE,
    UPS_TRACKING_TYPE,
    INBOUND_TRANSIT_ALERT,
    INBOUND_RECEIVING_PUTAWAY_ALERT,
    CLARITY_INSIGHTS_TYPE,
    RICHPANEL_SYNC_TYPE,
    MOTION_SYNC_TYPE,
    INTELLIGEMS_SYNC_TYPE,
} from "../consts/cronTypes";

export { getAllowedUserPermissions };

export const arrayHasItems = (array) => {
    return array && array instanceof Array && array.length > 0;
}

export const removeLoadingKey = (loadingKey, keys) => {
    if (!arrayHasItems(keys)) return [];
    const currentKeys = [...keys].filter((key) => key !== loadingKey);
    return currentKeys;
}

/** Removes a single occurrence (same thunk may be pending more than once before older GETs abort). */
export const removeOneLoadingKey = (loadingKey, keys) => {
    if (!arrayHasItems(keys)) return [];
    const idx = keys.indexOf(loadingKey);
    if (idx === -1) return keys;
    return [...keys.slice(0, idx), ...keys.slice(idx + 1)];
}

export const getYearsFromToCurrent = (startYear) => {
    if (!startYear) return [];
    const currentYear = new Date().getFullYear();
    const years = Array.from(
        { length: currentYear - startYear + 1 },
        (_, i) => {
            const year = currentYear - i;
            return { label: year, value: String(year) };
        }
    );
    return years;
}

export const scrollToTop = () => {
    if (window !== undefined) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
}

export const filterKeyFromObject = (obj, keyToFilter) => {
    if (!obj) return {}
    return Object.fromEntries(
        Object.entries(obj).filter(([key]) => key !== String(keyToFilter))
    )
}

export const getTotalOwedTextColor = (totalOwed) => {
    if (Number(totalOwed) > 0) return "!text-subLine";
    if (Number(totalOwed) === 0) return "!text-successValue";
    return "text-gray-500"
}

export const getMissingQuantityTextColor = (totalReceived) => {
    if (Number(totalReceived) > 0) return "!text-subLine";
    if (Number(totalReceived) === 0) return "!text-successValue";
    return "text-gray-500"
}

export const formatDateToShortLabel = (dateString) => {
    try {
        const date = new Date(dateString);
        if (isNaN(date)) return '';
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month}, ${year}`;
    } catch {
        return ""
    }
}

const extractTagNumber = (tag, tagToSearch) => {
    if (!tagToSearch) return null;
    const regex = new RegExp(`\\b${tagToSearch}-(\\d+)\\b`);
    // Handle both string tags and object tags with name property
    const tagValue = typeof tag === 'string' ? tag : tag?.name;
    if (!tagValue) return null;
    const match = tagValue.match(regex);
    return match ? parseInt(match[1], 10) : null;
};

export const getTheHighestCountTag = (tags, tagToSearch) => {
    // Handle array input
    if (Array.isArray(tags)) {
        if (!arrayHasItems(tags)) return 0;
        const regex = new RegExp(`\\b${tagToSearch}-(\\d+)\\b`);
        const tagItems = tags
            .map((item) => {
                const match = (item?.name || "").match(regex);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter((value) => value !== null);
        return tagItems.length > 0 ? Math.max(...tagItems) : 0;
    }

    // Handle string input
    const tagsArray = (tags || "")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
    if (!arrayHasItems(tagsArray)) return 0;
    const regex = new RegExp(`\\b${tagToSearch}-(\\d+)\\b`);
    const tagItems = tagsArray
        .map((item) => {
            const match = item.match(regex);
            return match ? parseInt(match[1], 10) : null;
        })
        .filter((value) => value !== null);
    return tagItems.length > 0 ? Math.max(...tagItems) : 0;
};

export const getEstimatedDateByShippingMethod = (
    shippingMethod, 
    dateShipped,
) => {
    if (!dateShipped || !shippingMethod) return ""
    const isSea = shippingMethod === SEA;
    const isAir = shippingMethod === AIR;
    const addedDays = (isSea ? SEA_COUNT : isAir ? AIR_COUNT : 0);
    const newDate = new Date(dateShipped);
    newDate.setHours(0, 0, 0, 0);
    newDate.setUTCDate(newDate.getUTCDate() + addedDays);
    return format(new Date(newDate), 'MMM d, yyyy');
}

export const anyShipmentProductHasBoxes = (products) => {
    return (products || []).some((product) => arrayHasItems(product.boxes));
}

export const isDaysToSend = (
    shipment,
    additionalDays
) => {
    const currentCreatedDate = new Date(shipment.dateCreated);
    currentCreatedDate.setHours(0, 0, 0, 0);
    const createdDatePlusDaysPassed = new Date(currentCreatedDate);
    createdDatePlusDaysPassed.setDate(createdDatePlusDaysPassed.getDate() + additionalDays);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((createdDatePlusDaysPassed.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays
};

export const getPastDueDate = (
    createdDate,
    daysPassed
) => {
    if (!createdDate) return 0;
    const currentDaysPassed = daysPassed || 0;
    const currentCreatedDate = new Date(createdDate);
    currentCreatedDate.setHours(0, 0, 0, 0);
    const createdDatePlusDaysPassed = new Date(currentCreatedDate);
    createdDatePlusDaysPassed.setDate(createdDatePlusDaysPassed.getDate() + currentDaysPassed);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const differenceInDays = Math.round(
        (createdDatePlusDaysPassed - currentDate) / (24 * 60 * 60 * 1000)
    );
    if (differenceInDays < 0) return differenceInDays * -1;
    return 0;
};

export const getEstimatedPODate = (createdDate, addedDays, type) => {
    if (!createdDate) return "N/A";
    const currentAddedDays = addedDays || 0;
    const newDate = new Date(createdDate);
    newDate.setHours(0, 0, 0, 0);
    const createdDatePlusDays = new Date(newDate);
    createdDatePlusDays.setDate(createdDatePlusDays.getDate() + currentAddedDays);
    if (type === "default") return createdDatePlusDays;
    return format(new Date(createdDatePlusDays), 'MMM d, yyyy');
}

export const formatPrice = (price) => {
    const newPrice = price ? price : 0;
    try {
        return Number(newPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    } catch {
        return newPrice
    }
}

export const getQBErrorMessageForToast = (payload) => {
    if (typeof payload === "string") return payload;
    if (payload?.error?.message) return payload.error.message;
    return ""
}

export const getCronStatusColor = (status) => {
  switch (status) {
    case "IN_PROGRESS":
        return 'bg-blue-500 text-white';
    case "COMPLETED":
        return 'bg-green-500 text-white';
    case "ERROR":
        return 'bg-red-500 text-white';
    default:
        return 'bg-gray-500 text-white';
  }
}

export const getInventoryStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return 'bg-green-100 text-green-800';
      case "IN_ACTIVE":
        return 'bg-red-100 text-red-800';
      case "SUNSET":
        return 'bg-yellow-100 text-yellow-800'
      case "PLANNED":
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-green-100 text-green-800';
    }
}

export const getPeriodByOpenToBuy = (lookBack, startDate, endDate) => {
    switch (lookBack) {
        case "custom":
            return (startDate || endDate) ? `${startDate || ""} to ${endDate || ""}` : "";
        case "last-90-days":
            return "Last 90 days";
        case "last-180-days":
            return "Last 180 days";
        case "last-12-months":
            return "Last 12 months";
        default:
            return ""
    }
}

export const getSelectedChannelByOpenToBuy = (channel) => {
    switch (channel) {
        case "shopify-amazon":
            return "All Channels (Shopify+Amazon)";
        case "shopify":
            return "Shopify Only";
        case "amazon":
            return "Amazon Only";
        case "wholesale":
            return "Wholesale Only";
        default:
            return ""
    }
}
  
export const getMix = (type) => {
    switch (type) {
        case "units":
            return "Units";
        case "revenue":
            return "Revenue";
        default:
            return ""
    }
}

export const getCronNameType = (type) => {
  switch (type) {
    case PROFIT_REPORT_ALL:
        return 'Profit And Loss Report';
    case FINANCE_TYPE:
        return 'Purchase Orders';
    case QUICKBOOKS_TYPE:
        return 'QuickBooks';
    case SHOPIFY_AMAZON_INVENTORY:
        return 'Stock Levels';
    case BEGIN_INVENTORY:
        return 'Begin Inventory';
    case SHOPIFY_REFUNDS:
        return 'Shopify Refunds';
    case META_INSIGHTS_TYPE:
        return 'Meta Insights';
    case META_ADS_TYPE:
        return 'Meta Ads';
    case AMAZON_SALES_REPORT:
        return 'Amazon Sales Report';
    case SHOPIFY_PRODUCTS:
        return 'Shopify Products';
    case SHOPIFY_LANDING_COST:
        return 'Shopify Landing Cost';
    case SHOPIFY_METAFIELDS:
        return 'Shopify Metafield Definitions';
    case OTB_ORDERS:
        return 'OTB Orders';
    case SHOPIFY_AMAZON_SALES:
        return 'Shopify/Amazon sales';
    case CASH_FLOW_REPORT_ALL:
        return 'Cash Flow Report';
    case AMAZON_ORDERS_TYPE:
        return 'Amazon Orders';
    case SHIPMENT_COSTS_TYPE:
        return 'Shipment Costs';
    case SHOPIFY_METAFIELDS_UPDATE:
        return 'Shopify Metafields Update';
    case "TEST":
        return 'Test';
    case EMAILS_TYPE:
        return 'Emails';
    case RECEIVED_INVENTORY_TYPE:
        return 'Received Inventory Emails';
    case RECEIVED_SHIPMENTS_TYPE:
        return 'Received Shipments Emails';
    case WAREHOUSE_INVENTORY_TYPE:
        return 'Warehouse Inventory Emails';
    case INVENTORY_COST_CHANGE_TYPE:
        return 'Inventory Cost Change Emails';
    case SHIPPING_EACH_COST_CHANGE_TYPE:
        return 'Shipping Each Cost Change Emails';
    case SHIPHERO_TOKEN_TYPE:
        return 'Shiphero Token';
    case EADIN_COGS_TYPE:
        return 'Eadin COGS Emails';
    case LOW_STOCK_ALERTS_TYPE:
        return 'Low Stock Alerts Emails';
    case AMAZON_FBA_LOW_STOCK_ALERTS_TYPE:
        return 'Amazon FBA Low Stock Alerts Emails';
    case LOW_STOCK_SOLD_OUT_ALERTS_TYPE:
        return 'Low Stock Sold Out Alerts Emails';
    case BALANCE_SHEET_REPORT_ALL:
        return 'Balance Sheet Report';
    case MISSING_INVENTORY_COST:
        return 'Missing Inventory Cost Emails';
    case ORDERS_RECONCILE_TYPE:
        return 'Orders Reconcile';
    case BOM_PACKAGING_TYPE:
        return 'BOM Packaging';
    case CHECK_PRODUCT_SALE_STATUS_TYPE:
        return 'Check Product Sale Status';
    case SOURCE_SKUS_BY_PRODUCT_TYPE:
        return 'Source SKUs by Product';
    case AMAZON_MONTH_END_CLOSE_SUMMARY_TYPE:
        return 'Amazon Month End Close Summary';
    case INBOUND_SHIPMENTS_TYPE:
        return 'Inbound Shipments Report';
    case MONTHLY_AP_PO_PAID_TYPE:
        return 'Monthly AP PO Paid';
    case MONTHLY_PO_AP_DUE_TYPE:
        return 'Monthly PO AP Due';
    case MONTHLY_PO_AP_PROGRESS_TYPE:
        return 'Monthly PO AP Progress';
    case SALES_BY_PRODUCT_TYPE:
        return 'Sales by Product by Month';
    case SALES_BY_PRODUCT_BY_DAY_TYPE:
        return 'Sales by Product by Day';
    case SALES_REVENUE_TYPE:
        return 'Product Sales By Month';
    case SALES_REVENUE_MONTHLY_TYPE:
        return 'Total Product % of Revenue Summary';
    case SHOPIFY_PROFIT_SUMMARY_TYPE:
        return 'Shopify Profit By Product Summary';
    case SHOPIFY_PROFIT_BY_SKU_SUMMARY_TYPE:
        return 'Shopify Profit By SKU Summary';
    case TOTAL_PRODUCT_REVENUE_TYPE:
        return 'Total Product % of Revenue Detail';
    case KIT_PRODUCTS_TYPE:
        return 'Kit Products';
    case PRODUCTS_TYPE:
        return 'Products';
    case KLAVIYO_TYPE:
        return 'Klaviyo';
    case POSTSCRIPT_TYPE:
        return 'Postscript';
    case GOOGLE_ADS_METRICS_TYPE:
        return 'Google Ads';
    case MICROSOFT_ADS_METRICS_TYPE:
        return 'Microsoft Ads';
    case APPLOVIN_METRICS_TYPE:
        return 'AppLovin';
    case TIKTOK_ADS_METRICS_TYPE:
        return 'TikTok Ads (coming soon)';
    case SNAPCHAT_ADS_METRICS_TYPE:
        return 'Snapchat Ads (coming soon)';
    case X_ADS_METRICS_TYPE:
        return 'X Ads (coming soon)';
    case GA4_METRICS_TYPE:
        return 'GA4';
    case STRIPE_SYNC_TYPE:
        return 'Stripe';
    case ALIA_METRICS_TYPE:
        return 'Alia';
    case TRYBE_METRICS_TYPE:
        return 'Trybe';
    case FATHOM_MEETINGS_TYPE:
        return 'Fathom';
    case UPS_TRACKING_TYPE:
        return 'UPS Tracking';
    case INBOUND_TRANSIT_ALERT:
        return 'Inbound Shipments Alert';
    case INBOUND_RECEIVING_PUTAWAY_ALERT:
        return 'Inbound Receiving & Put-away Alert';
    case CLARITY_INSIGHTS_TYPE:
        return 'Microsoft Clarity';
    case RICHPANEL_SYNC_TYPE:
        return 'Richpanel (coming soon)';
    case MOTION_SYNC_TYPE:
        return 'Motion (coming soon)';
    case INTELLIGEMS_SYNC_TYPE:
        return 'Intelligems (coming soon)';
    default:
        return '';
  }
}

export const getVarianceColor = (variance) => {
    if (variance > 0) return "text-green-700";
    if (variance < 0) return "text-red-700";
    return "text-gray-600";
}

export const userHasAccessTag = (user, tags) => {
    if (!user || !Array.isArray(tags)) {
        return false;
    }
    const userTagArray = (user?.tags || "").split(',').map(tag => tag.trim());
    return tags.some(tag => userTagArray.includes(tag));
};

export const reworkDate = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("T24:")) {
        const [datePart, timePart] = dateStr.split("T");
        const newDate = new Date(datePart);
        newDate.setDate(newDate.getDate());
        const fixedTime = timePart.replace("24:", "00:");
        return `${newDate.toISOString().split("T")[0]}T${fixedTime}`;
    }
    return dateStr;
}

export const getSortDirectionByTanStackTable = (desc) => {
    if (desc !== undefined) {
        if (desc) return "DESC"
        if (!desc) return "ASC"
    }
    return "ASC";
}

export const getKeyByValue = (obj, value) => {
    return Object.entries(obj).find(([key, val]) => val === value)?.[0] || null;
};


/** Matches api/helpers/common.js — 2 fractional digits when the value fits cents, otherwise 4. */
const MONETARY_TWO_DECIMAL_EPSILON = 1e-8;

export const getMonetaryFractionDigitsTwoOrFour = (value) => {
    // const n = Number(value ?? 0);
    // if (!Number.isFinite(n)) return 2;
    // const atTwoDecimals = Number(n.toFixed(2));
    // if (Math.abs(n - atTwoDecimals) < MONETARY_TWO_DECIMAL_EPSILON) return 2;
    return 4;
};

/** USD with exactly 2 or 4 digits after the decimal (and grouping), never 1 or 3. */
export const formatCurrencyUsdTwoOrFour = (amount) => {
    const n = Number(amount ?? 0);
    const digits = getMonetaryFractionDigitsTwoOrFour(n);
    if (!Number.isFinite(n)) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(0);
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(n);
};

export const formatCurrencyUsdFixedTwo = (amount) => {
    const n = Number(amount ?? 0);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
};